import { Layer } from './Layer';
import { HistoryStep, LayerSnapshot } from '../types';

export class HistoryManager {
  private history: HistoryStep[] = [];
  private currentIndex: number = -1;
  private maxSteps: number = 30;
  private onChangeCallbacks: ((canUndo: boolean, canRedo: boolean, steps: HistoryStep[], activeIndex: number) => void)[] = [];

  constructor(maxSteps: number = 30) {
    this.maxSteps = maxSteps;
  }

  public subscribe(cb: (canUndo: boolean, canRedo: boolean, steps: HistoryStep[], activeIndex: number) => void) {
    this.onChangeCallbacks.push(cb);
  }

  private notify() {
    const canUndo = this.currentIndex > 0;
    const canRedo = this.currentIndex < this.history.length - 1;
    this.onChangeCallbacks.forEach(cb => cb(canUndo, canRedo, [...this.history], this.currentIndex));
  }

  public record(name: string, layers: Layer[], width: number, height: number, icon?: string) {
    // Truncate any redos ahead of current index
    if (this.currentIndex < this.history.length - 1) {
      this.history = this.history.slice(0, this.currentIndex + 1);
    }

    const snapshot: LayerSnapshot[] = layers.map(layer => ({
      id: layer.id,
      name: layer.name,
      type: layer.type,
      visible: layer.visible,
      locked: layer.locked,
      opacity: layer.opacity,
      blendMode: layer.blendMode,
      x: layer.x,
      y: layer.y,
      width: layer.width,
      height: layer.height,
      imageDataUri: layer.canvas.toDataURL(),
      textData: layer.textData ? { ...layer.textData } : undefined
    }));

    const step: HistoryStep = {
      name,
      icon,
      timestamp: Date.now(),
      snapshot,
      canvasWidth: width,
      canvasHeight: height
    };

    this.history.push(step);
    if (this.history.length > this.maxSteps) {
      this.history.shift();
    } else {
      this.currentIndex++;
    }

    this.notify();
  }

  public canUndo(): boolean {
    return this.currentIndex > 0;
  }

  public canRedo(): boolean {
    return this.currentIndex < this.history.length - 1;
  }

  public undo(): HistoryStep | null {
    if (!this.canUndo()) return null;
    this.currentIndex--;
    this.notify();
    return this.history[this.currentIndex];
  }

  public redo(): HistoryStep | null {
    if (!this.canRedo()) return null;
    this.currentIndex++;
    this.notify();
    return this.history[this.currentIndex];
  }

  public jumpTo(index: number): HistoryStep | null {
    if (index >= 0 && index < this.history.length) {
      this.currentIndex = index;
      this.notify();
      return this.history[this.currentIndex];
    }
    return null;
  }

  public clear() {
    this.history = [];
    this.currentIndex = -1;
    this.notify();
  }
}
